import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Loader2, ArrowLeft, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const [step, setStep] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [twofaToken, setTwofaToken] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, verify2fa, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email.trim(), password);
      if (res?.twofaRequired) {
        setTwofaToken(res.twofaToken);
        setStep("2fa");
      } else {
        navigate("/admin", { replace: true });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verify2fa(twofaToken, code.trim());
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const backToPassword = () => {
    setStep("password");
    setCode("");
    setError("");
  };

  const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/20";

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Към сайта
        </Link>
        <div className="rounded-2xl bg-white p-8 shadow-2xl">
          {step === "password" ? (
            <>
              <div className="flex flex-col items-center text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
                  <ShieldCheck className="h-7 w-7 text-white" />
                </div>
                <h1 className="mt-4 font-display text-2xl font-extrabold text-slate-900">
                  Редакторски портал
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  Подуяне Днес · вход за редактори
                </p>
              </div>

              <form onSubmit={submitPassword} className="mt-8 space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Имейл
                  </label>
                  <input
                    type="email"
                    required
                    className={inputClass}
                    data-testid="login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@poduyane.bg"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Парола
                  </label>
                  <input
                    type="password"
                    required
                    className={inputClass}
                    data-testid="login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                </div>

                {error && (
                  <div
                    className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600"
                    data-testid="login-error"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0041A8] disabled:opacity-60"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Вход
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="flex flex-col items-center text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
                  <KeyRound className="h-7 w-7 text-white" />
                </div>
                <h1 className="mt-4 font-display text-2xl font-extrabold text-slate-900">
                  Двуфакторна автентикация
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  Въведете 6-цифрения код от приложението-автентикатор
                </p>
              </div>

              <form onSubmit={submit2fa} className="mt-8 space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Код за потвърждение
                  </label>
                  <input
                    type="text"
                    inputMode="text"
                    autoFocus
                    autoComplete="one-time-code"
                    required
                    className={`${inputClass} text-center text-lg font-semibold tracking-[0.3em]`}
                    data-testid="login-2fa-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="000000"
                  />
                  <p className="mt-1.5 text-xs text-slate-400">
                    Нямате достъп до приложението? Използвайте резервен код.
                  </p>
                </div>

                {error && (
                  <div
                    className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600"
                    data-testid="login-error"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-2fa-submit-btn"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0041A8] disabled:opacity-60"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Потвърди
                </button>
                <button
                  type="button"
                  onClick={backToPassword}
                  data-testid="login-2fa-back"
                  className="inline-flex w-full items-center justify-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Назад
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
